import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, CircleDot, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatDateBR } from "@/utils/dateUtils";
import { formatCents, formatPercent, Moeda, parseMoeda } from "@/utils/money";
import type { PropostaDetail, PropostaPendencia } from "@/types/finance";

interface ResumoPropostaProps {
  proposta: PropostaDetail;
  salvando: boolean;
  ultimaGravacao: Date | null;
  onResolverPendencia: (pendencia: PropostaPendencia) => void;
  acaoPrincipal?: ReactNode;
}

function contextoDoItem(proposta: PropostaDetail, itemId: string | null): string | null {
  if (!itemId) return null;
  for (const grupo of proposta.grupos) {
    const indice = grupo.itens.findIndex((item) => item.id === itemId);
    if (indice >= 0) {
      const item = grupo.itens[indice];
      const periodo = item.data_inicio ? `a partir de ${formatDateBR(item.data_inicio)}` : `período ${grupo.periodo_inicial + indice}`;
      return `${grupo.freelancer.nome.split(" ")[0]}, ${periodo}`;
    }
  }
  return null;
}

export function ResumoProposta({
  proposta,
  salvando,
  ultimaGravacao,
  onResolverPendencia,
  acaoPrincipal,
}: ResumoPropostaProps) {
  const itens = proposta.grupos.flatMap((grupo) => grupo.itens);
  const totalPeriodos = itens.length;
  const osPrevistas = itens.reduce(
    (soma, item) =>
      soma + (item.modo_agencia === "total" && item.modo_freelancer === "reter" ? 1 : 2),
    0,
  );
  const bloqueantes = proposta.pendencias.filter((pendencia) => pendencia.bloqueia_geracao);
  const avisos = proposta.pendencias.filter((pendencia) => !pendencia.bloqueia_geracao);

  const moeda = parseMoeda(proposta.moeda);
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
        <p className="text-sm font-semibold">Resumo</p>
        <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
          {salvando ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" />
              Salvando
            </>
          ) : ultimaGravacao ? (
            <>
              <CheckCircle2 className="h-3 w-3 text-emerald-600" />
              Salvo automaticamente
            </>
          ) : (
            "Salvo automaticamente"
          )}
        </p>
      </div>

      <div className="grid grid-cols-3 divide-x border-b text-center">
        <Contador valor={proposta.grupos.length} rotulo="Profissionais" />
        <Contador valor={totalPeriodos} rotulo="Períodos" />
        <Contador valor={osPrevistas} rotulo="OS previstas" />
      </div>

      <dl className="space-y-2 border-b px-4 py-3 text-sm">
        <LinhaValor rotulo="Profissionais" valor={formatCents(proposta.totais.profissional_cents, moeda)} />
        <LinhaValor rotulo="Taxa da agência" valor={formatCents(proposta.totais.plataforma_cents, moeda)} />
        {proposta.totais.impostos_cents > 0 && (
          <LinhaValor rotulo="Impostos" valor={formatCents(proposta.totais.impostos_cents, moeda)} />
        )}
        <LinhaValor rotulo="Comissão dos profissionais" valor={formatCents(proposta.totais.comissao_faisca_cents, moeda)} />
        <LinhaValor rotulo="Total da proposta" valor={formatCents(proposta.totais.total_cents, moeda)} forte />
        {moeda !== Moeda.BRL && (
          <>
            <LinhaValor rotulo="Cotação" valor={proposta.cotacao_omie != null ? `R$ ${formatPercent(proposta.cotacao_omie)}` : "Falta preencher"} />
            {proposta.cotacao_omie != null && (
              <LinhaValor rotulo="Total em reais" valor={formatCents(Math.round(proposta.totais.total_cents * proposta.cotacao_omie))} />
            )}
          </>
        )}
      </dl>

      {proposta.pendencias.length > 0 ? (
        <div className="space-y-2 border-b px-4 py-3">
          <p className="text-xs font-medium text-muted-foreground">
            {bloqueantes.length > 0
              ? `${bloqueantes.length} ${bloqueantes.length === 1 ? "item impede" : "itens impedem"} gerar o PDF`
              : "Pronta para gerar o PDF"}
          </p>
          <ul className="space-y-1.5">
            {[...bloqueantes, ...avisos].map((pendencia, indice) => {
              const contexto = contextoDoItem(proposta, pendencia.item_id);
              return (
                <li key={`${pendencia.codigo}-${pendencia.item_id ?? ""}-${indice}`}>
                  <button
                    type="button"
                    onClick={() => onResolverPendencia(pendencia)}
                    className={cn(
                      "flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors",
                      pendencia.bloqueia_geracao
                        ? "bg-amber-50 text-amber-900 hover:bg-amber-100"
                        : "text-muted-foreground hover:bg-muted",
                    )}
                  >
                    {pendencia.bloqueia_geracao ? (
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    ) : (
                      <CircleDot className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    )}
                    <span>
                      {pendencia.mensagem}
                      {contexto && <span className="block text-[11px] opacity-80">{contexto}</span>}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {avisos.length > 0 && bloqueantes.length === 0 && (
            <p className="text-[11px] text-muted-foreground">
              Os itens acima não impedem o PDF, mas faltam para lançar as OS no Omie.
            </p>
          )}
        </div>
      ) : (
        <p className="flex items-center gap-1.5 border-b px-4 py-3 text-xs text-emerald-700">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Tudo preenchido
        </p>
      )}

      {acaoPrincipal && <div className="p-4">{acaoPrincipal}</div>}
    </Card>
  );
}

function Contador({ valor, rotulo }: { valor: number; rotulo: string }) {
  return (
    <div className="px-2 py-3">
      <p className="text-xl font-semibold tabular-nums">{String(valor).padStart(2, "0")}</p>
      <p className="text-[11px] text-muted-foreground">{rotulo}</p>
    </div>
  );
}

function LinhaValor({ rotulo, valor, forte }: { rotulo: string; valor: string; forte?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className={cn("text-muted-foreground", forte && "font-medium text-foreground")}>{rotulo}</dt>
      <dd className={cn("tabular-nums", forte ? "font-semibold" : "font-medium")}>{valor}</dd>
    </div>
  );
}
